/**
 * PATH: packages/email-hub/src/feature/components/EmailConnectionBanner.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-EMAILCONNECTIONBANNER-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/EmailConnectionBanner.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/EmailConnectionBanner.tsx:
 *     thin re-export shim that points back here.
 */

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Mail,
  RefreshCw,
  WifiOff,
} from 'lucide-react'
import OutlookBanner from './OutlookBanner'
import type { OutlookStatus } from '../hooks/useEmails'

type GmailStatusForBanner = {
  connected: boolean | null
  email: string | null
  isExpired: boolean
  reachable: boolean
  expiringSoon?: boolean
  // Mirrors `OutlookStatus.orgConnected` — true when ANY user in the
  // current organisation has a working Gmail token, even if THIS user
  // hasn't personally connected. Same rationale as the Outlook field:
  // suppresses the per-page nag when org-level send is functional.
  // qf!2897 follow-up to qb!2796 / qf!2795.
  orgConnected?: boolean
}

type Props = {
  outlookStatus: OutlookStatus & { expiringSoon?: boolean }
  gmailStatus: GmailStatusForBanner
  onConnectOutlook: () => void | Promise<void>
  onConnectGmail: () => void | Promise<void>
  /** When true, renders nothing for the all-healthy state. */
  compact?: boolean
  className?: string
}

function isProviderDisconnected(
  s: {
    connected: boolean | null
    isExpired: boolean
    reachable: boolean
    orgConnected?: boolean
  },
  userPersonallyConnectedElsewhere: boolean,
) {
  // BUG-2026-05-28-EMAIL-NEW-USER-NO-CONNECT-CTA — Mitchell Brood at
  // Newmark was invited as Contracts Administrator, signed in with
  // his fresh credentials, and had NO way to connect his Gmail. The
  // original gate suppressed the disconnect CTA whenever
  // ``orgConnected=true`` (someone else in the org has the token),
  // conflating two different cases:
  //   (a) Mark 2026-05-13 — Mark had Gmail personally connected and
  //       didn't want the Outlook nag. ``orgConnected`` was a proxy
  //       for "the user has another provider working".
  //   (b) Mitch 2026-05-28 — Mitch had NOTHING personally connected.
  //       The same ``orgConnected`` suppression silenced his only
  //       path to onboard his mailbox.
  //
  // The correct discriminator is ``userPersonallyConnectedElsewhere``:
  // if the user has AT LEAST ONE provider they personally signed in
  // to, suppress the nag for any other provider that's down (case
  // a). If they have NO personal connection, surface the CTA so the
  // freshly-invited user can onboard (case b). ``orgConnected`` is
  // now irrelevant to this decision — sends can still fall through
  // to the org token for users who DO have at least one personal
  // connection, but the new-user state must not be hidden.
  // A healthy personal connection on THIS provider → never nag, even
  // if a single status poll just came back unreachable. The status
  // hook (useEmails.ts) now preserves the last-known connected state on
  // a transient fetch error, so `connected === true` here means a real
  // working mailbox — a momentary `reachable: false` must not flash the
  // CTA at a connected user (the reported connect-banner flap).
  if (s.connected === true && !s.isExpired) return false
  // `reachable === false` (status service unreachable, OR a provider
  // the user never configured): surface the CTA ONLY when the user has
  // no working personal connection on the OTHER provider. This keeps a
  // dual-mailbox user (Mark 2026-05-13) from being nagged about a blip
  // on the provider they don't actively use, while a brand-new user
  // with nothing connected anywhere (Mitch 2026-05-28) still sees it.
  if (s.reachable === false) return !userPersonallyConnectedElsewhere
  // A genuinely expired session is a real, blocking signal — keep
  // nagging exactly as before (unchanged behaviour).
  if (s.isExpired) return true
  if (s.connected === false) {
    // Suppress the "needs attention" nag ONLY when the user has
    // another provider they personally signed in to. New-user
    // onboarding (no personal connection on either provider) MUST
    // surface the CTA.
    return !userPersonallyConnectedElsewhere
  }
  return false
}

function userIsPersonallyConnected(s: {
  connected: boolean | null
  isExpired: boolean
}) {
  return s.connected === true && !s.isExpired
}

export default function EmailConnectionBanner({
  outlookStatus,
  gmailStatus,
  onConnectOutlook,
  onConnectGmail,
  compact,
  className,
}: Props) {
  const outlookPersonal = userIsPersonallyConnected(outlookStatus)
  const gmailPersonal = userIsPersonallyConnected(gmailStatus)
  const outlookDisconnected = isProviderDisconnected(outlookStatus, gmailPersonal)
  const gmailDisconnected = isProviderDisconnected(gmailStatus, outlookPersonal)

  // Both connected and healthy → nothing to show in compact mode.
  if (compact && !outlookDisconnected && !gmailDisconnected) return null

  // Both disconnected → split banner with two CTAs (the user-visible ask).
  if (outlookDisconnected && gmailDisconnected) {
    // BUG-0011: the previous tone was a dark-mode palette only
    // (`text-amber-200` ≈ #fde68a on a `bg-amber-500/10` ≈ #fefce8 — pure-
    // yellow body text on an almost-white background, completely unreadable
    // on the default light theme). Switch to the same light/dark-aware
    // token set used by IntegrationsBanner.BannerCard so the banner reads
    // correctly in BOTH themes without forking copy.
    const tone =
      'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100'
    return (
      <div
        className={`flex items-start gap-3 p-3 border rounded-lg ${tone} ${className ?? ''}`}
        role="alert"
        aria-live="polite"
      >
        <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Connect your email</div>
          <div className="text-xs opacity-80 mt-0.5">
            Link a Microsoft (Outlook) or Gmail mailbox to send and sync email
            from this hub. SMTP fallback is used until you connect.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => { void onConnectOutlook() }}
            className="text-xs px-2.5 py-1 rounded-md bg-[#0078D4] text-white hover:bg-[#006CBE] transition-colors flex items-center gap-1.5"
            aria-label="Connect Microsoft Outlook"
          >
            <ExternalLink className="h-3 w-3" />
            Connect Microsoft
          </button>
          <button
            type="button"
            onClick={() => { void onConnectGmail() }}
            className="text-xs px-2.5 py-1 rounded-md bg-[#EA4335] text-white hover:bg-[#D33C2D] transition-colors flex items-center gap-1.5"
            aria-label="Connect Gmail"
          >
            <ExternalLink className="h-3 w-3" />
            Connect Gmail
          </button>
        </div>
      </div>
    )
  }

  // Mixed state: at least one provider is connected. Show only the banners
  // for the providers that need attention. Outlook keeps its existing banner
  // (rich variant rendering); Gmail gets a compact equivalent inline below.
  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      {outlookDisconnected && (
        <OutlookBanner
          status={outlookStatus}
          onConnect={onConnectOutlook}
          compact={compact}
        />
      )}
      {gmailDisconnected && <GmailMiniBanner status={gmailStatus} onConnect={onConnectGmail} />}
    </div>
  )
}

function GmailMiniBanner({
  status,
  onConnect,
}: {
  status: GmailStatusForBanner
  onConnect: () => void | Promise<void>
}) {
  const variant: 'unreachable' | 'expired' | 'disconnected' | 'expiringSoon' | 'connected' =
    status.reachable === false
      ? 'unreachable'
      : status.connected === false
        ? 'disconnected'
        : status.isExpired
          ? 'expired'
          : status.expiringSoon
            ? 'expiringSoon'
            : 'connected'

  if (variant === 'connected') return null

  // BUG-0011: same unreadable-on-light-theme failure mode as the both-
  // disconnected variant above. Use light/dark-aware tokens so the
  // mini banner stays legible in both themes.
  const tone =
    variant === 'unreachable'
      ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100'
      : variant === 'expired'
        ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-100'
        : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100'

  const Icon =
    variant === 'unreachable'
      ? WifiOff
      : variant === 'expiringSoon'
        ? Clock
        : variant === 'expired'
          ? AlertCircle
          : Mail

  const title =
    variant === 'unreachable'
      ? 'Gmail status unavailable'
      : variant === 'expired'
        ? 'Gmail session expired'
        : variant === 'disconnected'
          ? 'Gmail not connected'
          : 'Gmail session expires soon'

  const body =
    variant === 'unreachable'
      ? "Couldn't reach the Gmail status service. Try again in a moment."
      : variant === 'expired'
        ? 'Your Google sign-in has expired. Reconnect to send and sync email.'
        : variant === 'disconnected'
          ? 'Connect Gmail to send and sync from your Google mailbox.'
          : 'Your Google session expires in less than 5 minutes. Reconnect now to avoid mid-send failures.'

  const actionLabel =
    variant === 'expired' || variant === 'expiringSoon'
      ? 'Reconnect'
      : variant === 'disconnected'
        ? 'Connect Gmail'
        : 'Retry'

  return (
    <div
      className={`flex items-start gap-3 p-3 border rounded-lg ${tone}`}
      role={variant === 'expired' || variant === 'disconnected' ? 'alert' : 'status'}
    >
      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs opacity-80 mt-0.5">{body}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => { void onConnect() }}
          className="text-xs px-2.5 py-1 rounded-md bg-[#EA4335] text-white hover:bg-[#D33C2D] transition-colors flex items-center gap-1.5"
        >
          {variant === 'unreachable' ? <RefreshCw className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
          {actionLabel}
        </button>
      </div>
    </div>
  )
}
